import { useCallback, useMemo } from 'react';
import {
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_ColumnFiltersState,
  type MRT_ColumnPinningState,
  type MRT_ColumnSizingState,
  type MRT_DensityState,
  type MRT_PaginationState,
  type MRT_SortingState,
} from 'material-react-table';
import { useTheme, type SxProps, type Theme } from '@mui/material';
import { getDefaultMRTOptions } from '../../table-utils/mrtDefaults';
import type { AflDailyRow } from './columns';

function getDefaultColumnOrder(): string[] {
  return [
    'Date', 'RO Number', 'Last Name', 'Line Number',
    'Type Code', 'Line Type', 'Line Pay Method', 'providerSource',
    'Labor Amount', 'Op Code', 'Service Type',
    'Part Number', 'Special Order', 'Manufacturer', 'TransCode', 'Description',
    'coveredLine', 'coveredPart', 'OEM Covered', 'Desc Covered',
    'Serv Sales', 'Serv Prem', 'Parts Sales', 'Parts Premium',
  ];
}

export interface ReportTableState {
  globalFilter: string;
  pagination: MRT_PaginationState;
  sorting: MRT_SortingState;
  columnVisibility: Record<string, boolean>;
  density: 'compact' | 'comfortable' | 'spacious';
  columnOrder: string[];
  columnFilters: MRT_ColumnFiltersState;
  columnSizing: MRT_ColumnSizingState;
  columnPinning: MRT_ColumnPinningState;
}

export interface ReportTableSetters {
  setGlobalFilter: (v: string) => void;
  setPagination: React.Dispatch<React.SetStateAction<MRT_PaginationState>>;
  setSorting: React.Dispatch<React.SetStateAction<MRT_SortingState>>;
  setColumnFilters: (v: MRT_ColumnFiltersState | ((prev: MRT_ColumnFiltersState) => MRT_ColumnFiltersState)) => void;
  setColumnOrder: (v: string[]) => void;
  setColumnVisibility: (v: Record<string, boolean>) => void;
  setDensity: (v: 'compact' | 'comfortable' | 'spacious') => void;
  setColumnSizing: (v: MRT_ColumnSizingState) => void;
  setColumnPinning: (v: MRT_ColumnPinningState) => void;
}

interface UseReportTableOptions {
  columns: MRT_ColumnDef<AflDailyRow>[];
  data: AflDailyRow[];
  total: number;
  loading: boolean;
  state: ReportTableState;
  setters: ReportTableSetters;
  zebraRows: boolean;
  renderTopToolbarCustomActions: () => React.ReactNode;
}

/**
 * Configures useMaterialReactTable for the OLSG Daily Report.
 * Extracted from Reports.tsx to reduce component size.
 */
export function useReportTable({
  columns,
  data,
  total,
  loading,
  state,
  setters,
  zebraRows,
  renderTopToolbarCustomActions,
}: UseReportTableOptions) {
  const theme = useTheme();

  const handleColumnOrderChange = useCallback(
    (updater: any) => {
      const next = typeof updater === 'function' ? updater(state.columnOrder) : (updater as string[]);
      setters.setColumnOrder(next);
      localStorage.setItem('afl_column_order', JSON.stringify(next));
    },
    [state.columnOrder, setters],
  );

  const handleColumnVisibilityChange = useCallback(
    (updater: any) => {
      const next = typeof updater === 'function' ? updater(state.columnVisibility) : updater;
      setters.setColumnVisibility(next);
      localStorage.setItem('afl_column_visibility', JSON.stringify(next));
    },
    [state.columnVisibility, setters],
  );

  const handleDensityChange = useCallback(
    (updater: any) => {
      const next = typeof updater === 'function'
        ? (updater as (old: MRT_DensityState) => MRT_DensityState)(state.density)
        : updater;
      setters.setDensity(next);
      localStorage.setItem('afl_density', next);
    },
    [state.density, setters],
  );

  const handleColumnSizingChange = useCallback(
    (updater: any) => {
      const next = typeof updater === 'function' ? updater(state.columnSizing) : updater;
      setters.setColumnSizing(next);
      localStorage.setItem('afl_column_sizing', JSON.stringify(next));
    },
    [state.columnSizing, setters],
  );

  const handleColumnPinningChange = useCallback(
    (updater: any) => {
      const next = typeof updater === 'function' ? updater(state.columnPinning) : updater;
      setters.setColumnPinning(next);
      localStorage.setItem('afl_column_pinning', JSON.stringify(next));
    },
    [state.columnPinning, setters],
  );

  // Memoized so Emotion doesn't re-serialize these large style objects on every render.
  const containerSx = useMemo<SxProps<Theme>>(
    () => ({ maxWidth: '100%', overflow: 'auto', backgroundColor: theme.palette.background.paper }),
    [theme],
  );

  const tableSx = useMemo<SxProps<Theme>>(
    () => ({
      tableLayout: 'fixed',
      width: '100%',
      minWidth: '100%',
      backgroundColor: theme.palette.background.paper,
      '& .MuiTableCell-root': {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        padding: '6px 3px',
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
      },
      '& .MuiTableHead-root .MuiTableCell-root': {
        padding: '4px 3px',
        fontSize: '0.75rem',
        fontWeight: 600,
        backgroundColor: theme.palette.action.selected,
        color: theme.palette.text.primary,
        lineHeight: 1.2,
        whiteSpace: 'normal',
        overflow: 'visible',
        textOverflow: 'clip',
        verticalAlign: 'bottom',
        wordBreak: 'break-word',
      },
      '& .MuiTableHead-root .Mui-TableHeadCell-Content, & .MuiTableHead-root .Mui-TableHeadCell-Content-Labels, & .MuiTableHead-root .Mui-TableHeadCell-Content-Wrapper':
        { whiteSpace: 'normal', overflow: 'visible', textOverflow: 'clip' },
      '& .Mui-TableHeadCell-ResizeHandle-Wrapper': { opacity: 1 },
      '& .Mui-TableHeadCell-ResizeHandle-Divider': {
        borderColor: theme.palette.divider,
        borderRightWidth: '2px',
      },
      '& tr[data-filter-row="true"] td': {
        padding: '2px 2px',
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
      },
      '& .MuiFormControl-root > .MuiFormLabel-root': { display: 'none' },
      '& .MRT_FilterModeMenuButton': { display: 'none' },
      '& .MuiTableBody-root .MuiTableCell-root': { fontSize: '0.8rem', lineHeight: 1.3 },
      '& .Mui-TableBodyCell-Content, & .Mui-TableHeadCell-Content': { color: theme.palette.text.primary },
      '& .Mui-TableBodyCell-Content-Wrapper, & .Mui-TableHeadCell-Content-Wrapper': {
        color: theme.palette.text.primary,
      },
    }),
    [theme],
  );

  const bodyRowProps = useCallback(
    ({ row }: any) => {
      if (!zebraRows) {
        const ro = (row.original as any)?.['RO Number'] as string | undefined | null;
        const fallback = (row.original as any)?.VIN ?? row.id ?? row.index ?? '';
        const key = typeof ro === 'string' && ro.length > 0 ? ro : String(fallback ?? '');
        if (!key || key.length === 0) return {};
        let hash = 0;
        for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) | 0;
        const shaded = (hash & 1) === 1;
        return {
          sx: {
            backgroundColor: shaded
              ? theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.03)'
                : '#fafafa'
              : 'transparent',
          },
        };
      }
      return {
        sx: { backgroundColor: row.index % 2 === 1 ? 'var(--afl-row-alt-bg)' : 'transparent' },
      };
    },
    [theme, zebraRows],
  );

  return useMaterialReactTable<AflDailyRow>({
    ...getDefaultMRTOptions<AflDailyRow>(),
    columns: columns as any,
    data,
    state: {
      isLoading: loading,
      globalFilter: state.globalFilter,
      pagination: state.pagination,
      sorting: state.sorting,
      columnVisibility: state.columnVisibility,
      density: state.density,
      columnOrder: state.columnOrder,
      columnFilters: state.columnFilters,
      columnSizing: state.columnSizing,
      columnPinning: state.columnPinning,
    },
    layoutMode: 'grid',
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    columnFilterDisplayMode: 'popover',
    enableColumnFilterModes: false,
    muiFilterTextFieldProps: {
      autoFocus: false,
      size: 'small',
      variant: 'standard',
      placeholder: '',
      InputProps: { disableUnderline: false },
      sx: {
        minWidth: 50, maxWidth: 120,
        '& .MuiInputBase-root': { fontSize: '0.65rem', padding: '0px', minHeight: '20px' },
        '& .MuiInputBase-input': { padding: '2px 4px', fontSize: '0.65rem', height: '20px' },
        '& .MuiInput-underline:before': { borderBottom: '1px solid rgba(255, 255, 255, 0.2)' },
        '& .MuiInput-underline:hover:before': { borderBottom: '1px solid rgba(255, 255, 255, 0.4)' },
      },
    } as any,
    muiFilterAutocompleteProps: {
      size: 'small',
      sx: {
        minWidth: 50, maxWidth: 120,
        '& .MuiInputBase-root': { fontSize: '0.65rem', padding: '0px', minHeight: '22px' },
        '& .MuiInputBase-input': { padding: '2px 4px !important', fontSize: '0.65rem' },
        '& .MuiAutocomplete-endAdornment': { display: 'none' },
        '& .MuiChip-root': { height: '16px', fontSize: '0.6rem', margin: '1px' },
        '& .MuiChip-label': { padding: '0 4px' },
      },
    } as any,
    muiTableContainerProps: { sx: containerSx },
    muiTableProps: { sx: tableSx },
    onGlobalFilterChange: setters.setGlobalFilter,
    onPaginationChange: setters.setPagination,
    onSortingChange: setters.setSorting,
    onColumnFiltersChange: setters.setColumnFilters,
    onColumnOrderChange: handleColumnOrderChange,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    onDensityChange: handleDensityChange,
    onColumnSizingChange: handleColumnSizingChange,
    onColumnPinningChange: handleColumnPinningChange,
    rowCount: total,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    enableRowVirtualization: false,
    initialState: {
      density: state.density,
      columnPinning: state.columnPinning,
      columnVisibility: state.columnVisibility,
      sorting: state.sorting.length ? state.sorting : [{ id: 'Date', desc: false }],
      columnOrder: state.columnOrder.length ? state.columnOrder : getDefaultColumnOrder(),
      columnSizing: state.columnSizing,
      showColumnFilters: true,
    },
    muiTablePaperProps: { elevation: 0, sx: { borderRadius: '0' } },
    enableColumnDragging: true,
    enableColumnOrdering: true,
    muiTableBodyRowProps: bodyRowProps,
    renderTopToolbarCustomActions,
  });
}
